/**
 * @progression/core — the L1 verdict kernel.
 *
 * A 1:1 TypeScript port of the proved Lean engine `EngineV2`
 * (Reference/ProgressionEngine_v1.02_May-24.lean). Every function below names the
 * Lean object / theorem it realizes; the property suite re-derives those laws.
 *
 * Purity (INV-CORE, INV-DET): these functions take only their declared arguments,
 * read no time / randomness / locale / iteration order / ambient mutable state,
 * and return frozen immutable values. The verdict is a function of (claim, config)
 * and nothing else.
 */
import { messageId } from '@progression/contracts';
import { configDigest as digest } from './digest.js';
import { frozenConfig, deepFreeze } from './immutable.js';
import { applyEdgeToProfile } from './modal.js';
/**
 * `defaultConfig S` (Lean `defaultConfig` / `statusActiveDefault`): the active set
 * is EXACTLY the `adopted-formal` ids. `adopted-interpretive` and `suspended`
 * stay out of the base — the no-ambiguity rule (`formal_is_default`,
 * `interpretive_not_default`, `suspended_not_default`).
 */
export function defaultConfig(status) {
    const active = [];
    for (const [id, s] of status) {
        if (s === 'adopted-formal')
            active.push(id);
    }
    return frozenConfig(active);
}
/**
 * `challenge k C = fun k' => C k' ∧ k' ≠ k` (Lean `challenge`): drop one
 * constraint from the active set. Weaker config (`challenge_sub`); preserves
 * every explorable claim (`challenge_preserves`); can revive (`challenge_revives`).
 */
export function challenge(id, c) {
    const next = [];
    for (const k of c) {
        if (k !== id)
            next.push(k);
    }
    return frozenConfig(next);
}
/**
 * `activate` (Lean `activate`): switch a constraint on. At the config level this
 * adds the id to the active set (Set dedups, so it is idempotent).
 */
export function activate(id, c) {
    return frozenConfig([...c, id]);
}
/**
 * `composeConfig C D = fun k => C k ∨ D k` (Lean `composeConfig`): union of the
 * two active sets. Commutative / associative / idempotent / monotone
 * (`compose_*`). Adding a base only ever restricts (INV-ADD-NOT-ALTER, model half).
 */
export function compose(a, b) {
    return frozenConfig([...a, ...b]);
}
/**
 * `importEdge` (Lean `importEdge`): ONLY a `constraint` edge composes the external
 * id into the config (`constraint_edge_stronger`, `external_constraint_can_refute`).
 * `resource` / `heuristic` / `shared-foundation` edges are configuration-inert —
 * they return the config unchanged (`resource_inert`, `heuristic_inert`,
 * `shared_inert`, `inert_preserves`).
 */
export function importEdge(kind, externalId, c) {
    if (kind === 'constraint' && externalId !== undefined) {
        return frozenConfig([...c, externalId]);
    }
    // Inert: a fresh frozen copy with identical contents (same verdict, same digest).
    return frozenConfig([...c]);
}
/**
 * `evaluate` (Lean `refutedUnder` / `consistentUnder`): the verdict is `refuted`
 * iff the active set meets any id in `claim.violates`, else `explorable`.
 *   - `conflictingConstraints`: the met ids (empty ⇒ explorable);
 *   - `missingBase`: `claim.needs` ids not active (for L4 to derive `outside`);
 *   - `profile`: carried through unchanged;
 *   - `configDigest`: stamps the config (order-independent, INV-NOEQUIV).
 *
 * Determinism: both id lists are computed by iterating the claim's own
 * (fixed-order) `violates` / `needs` arrays and testing membership, so their
 * order does not depend on the Config's insertion order (INV-DET).
 */
export function evaluate(claim, c) {
    const conflictingConstraints = [];
    for (const k of claim.violates) {
        if (c.has(k))
            conflictingConstraints.push(k);
    }
    const missingBase = [];
    for (const k of claim.needs) {
        if (!c.has(k))
            missingBase.push(k);
    }
    const verdict = {
        state: conflictingConstraints.length > 0 ? 'refuted' : 'explorable',
        conflictingConstraints: Object.freeze(conflictingConstraints),
        missingBase: Object.freeze(missingBase),
        profile: Object.freeze({ ...claim.profile }),
        configDigest: digest(c),
    };
    return deepFreeze(verdict);
}
// Reason ids for a rejected warrant-promotion (stable MessageIds for L5).
const REASON = {
    refutationTerminal: messageId('promote.proof-cannot-cure-refutation'),
    keystone: messageId('promote.keystone-needs-empirical'),
    empiricalNotFixed: messageId('promote.empirical-not-fixed'),
    modalNotFixed: messageId('promote.modal-not-fixed'),
    warrantNotImproved: messageId('promote.warrant-not-improved'),
};
/**
 * `promoteW` (Lean `promoteW`): a valid warrant-promotion improves (or keeps) W
 * while FIXING empirical and modal content — `p'.w ≤ p.w ∧ p'.e = p.e ∧ p'.m = p.m`
 * (`promoteW_fixes_content`). A request that breaks this is rejected with a stable
 * reason id, honouring the keystone guarantees:
 *   - refutation is terminal under proof (`proof_cannot_cure_refutation`):
 *     a `refuted` claim cannot be warrant-promoted out of `refuted`;
 *   - the empirical boundary is uncrossable by warrant work
 *     (`keystone_needs_empirical`, `program_needs_empirical`): no `promoteW`
 *     turns `testable`/`program` into `tested` — only an experiment (an E-move) can.
 */
export function promoteW(from, to) {
    if (from.e === 'refuted' && to.e !== 'refuted') {
        return deepFreeze({ ok: false, reason: REASON.refutationTerminal });
    }
    if ((from.e === 'testable' || from.e === 'program') && to.e === 'tested') {
        return deepFreeze({ ok: false, reason: REASON.keystone });
    }
    if (to.e !== from.e) {
        return deepFreeze({ ok: false, reason: REASON.empiricalNotFixed });
    }
    if (to.m !== from.m) {
        return deepFreeze({ ok: false, reason: REASON.modalNotFixed });
    }
    if (to.w > from.w) {
        return deepFreeze({ ok: false, reason: REASON.warrantNotImproved });
    }
    return deepFreeze({ ok: true });
}
/** Stable, order-independent digest (Lean has no analogue; INV-NOEQUIV plumbing). */
export function configDigest(c) {
    return digest(c);
}
/** The kernel as a single frozen object implementing the `Kernel` contract. */
export const kernel = Object.freeze({
    defaultConfig,
    challenge,
    activate,
    compose,
    importEdge,
    evaluate,
    promoteW,
    applyEdgeToProfile,
    configDigest,
});
//# sourceMappingURL=kernel.js.map